import React from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { View, Text, Button, VoiceRunLogo } from "./base";
import { Icon } from "./base/Icon";
import { Link } from "./base/Link";
import { ReactComponent as PrimVoicesLogo } from "../assets/svg/logo.svg";
import { useAuth } from "../hooks/useAuth";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { useAppSelector, useAppDispatch } from "../redux";
import { sessionActions } from "../redux/reducers/sessionSlice";
import { useTheme } from "../contexts/ThemeContext";
import { useCommandPalette } from "./CommandPalette";
import { NotificationBell } from "./NotificationBell";

export type SidebarLink = {
  label: string;
  to: string;
};

export type SidebarSection = {
  title: string;
  links: SidebarLink[];
};

type LeftNavbarProps = {
  sections?: SidebarSection[];
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

export const LeftNavbar = ({ sections = [], className, isOpen = true, onClose, isCollapsed = false, onToggleCollapse }: LeftNavbarProps) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { organization } = useEffectiveOrganization();
  const { theme, toggleTheme } = useTheme();
  const { open: openCommandPalette } = useCommandPalette();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = React.useState(false);
  const [orgSearch, setOrgSearch] = React.useState("");
  const dispatch = useAppDispatch();
  const userButtonRef = React.useRef<HTMLDivElement>(null);
  const orgButtonRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  // Get all organizations from Redux state
  const organizations = useAppSelector((state) => state.organization.entities);
  const organizationList = Object.values(organizations).filter((org) => org && !org.deletedAt);

  const isLinkActive = (linkTo: string) => {
    // For exact route matches (like /agents), only match the exact path
    // For other routes, check if the pathname starts with the link path
    if (linkTo === "/agents") {
      return pathname === linkTo || /^\/agents\/[^\/]+\/(overview|environments|function|create-recipe|testing|sessions)/.test(pathname);
    }
    return pathname === linkTo || pathname.startsWith(`${linkTo}/`);
  };

  // Filter organizations based on search
  const filteredOrganizations = organizationList.filter((org) => org.name.toLowerCase().includes(orgSearch.toLowerCase()));

  // Sort organizations by agent count (descending) then by name
  const sortedOrganizations = filteredOrganizations.sort((a, b) => {
    if (b.agentCount !== a.agentCount) {
      return b.agentCount - a.agentCount;
    }
    return a.name.localeCompare(b.name);
  });

  // Handle organization selection
  const handleOrgSelect = (orgId: string) => {
    setShowOrgDropdown(false);
    dispatch(sessionActions.setSelectedOrganization(orgId));
    window.location.reload();
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside portal dropdowns
      const target = event.target as Element;
      const isInUserPortal = target.closest(".user-menu-portal");
      const isInOrgPortal = target.closest(".org-dropdown-portal");

      if (showUserMenu && !target.closest(".user-menu-container") && !isInUserPortal) {
        setShowUserMenu(false);
      }
      if (showOrgDropdown && !target.closest(".org-dropdown-container") && !isInOrgPortal) {
        setShowOrgDropdown(false);
      }
      // Handle mobile sidebar close
      if (onClose && !target.closest(".left-navbar-container") && !target.closest(".hamburger-button")) {
        onClose();
      }
    };

    if (showUserMenu || showOrgDropdown || (onClose && isOpen)) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showUserMenu, showOrgDropdown, onClose, isOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {onClose && isOpen && <View className="fixed inset-0 bg-black/50 z-40 lg:hidden" />}

      <View
        ref={sidebarRef}
        className={clsx(
          "h-screen bg-gray-50 dark:bg-black border-r border-gray-200 dark:border-[#1a1a1a] flex flex-col left-navbar-container transition-all duration-300",
          // Width based on collapsed state
          isCollapsed ? "w-[60px]" : "w-[220px]",
          // Mobile: fixed overlay, Desktop: fixed
          onClose ? "fixed top-0 left-0 z-50 lg:fixed lg:top-0 lg:left-0 lg:z-auto" : "fixed top-0 left-0",
          // Mobile slide animation
          onClose && !isOpen && "-translate-x-full lg:translate-x-0",
          onClose && isOpen && "translate-x-0",
          className,
        )}
      >
        {/* Collapse toggle button */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="absolute top-7 -right-3.5 items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-50 hidden lg:flex"
          >
            <Icon
              name={isCollapsed ? "chevron-right" : "chevron-left"}
              size={12}
              className="text-gray-600 dark:text-gray-400"
            />
          </button>
        )}

        <View className={clsx("py-7", isCollapsed ? "px-4" : "px-6")}>
          {/* Mobile close button */}
          {onClose && !isCollapsed && (
            <button
              onClick={onClose}
              className="absolute top-6 right-6 lg:hidden p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon
                name="close"
                size={20}
                className="text-gray-600 dark:text-gray-400"
              />
            </button>
          )}

          <Link
            to="/"
            className="flex items-center gap-2 justify-start"
            onClick={() => onClose && onClose()}
          >
            <div className="flex items-center justify-center w-7 h-7 [--logo-fill:theme(colors.brand)] dark:[--logo-fill:theme(colors.cyan.400)] [--logo-stroke:theme(colors.slate.900)] dark:[--logo-stroke:theme(colors.slate.600)] [--logo-stroke-width:5]">
              {window.location.hostname.includes("voicerun") ? <VoiceRunLogo className="w-full h-full" /> : <PrimVoicesLogo className="w-full h-full" />}
            </div>
            {!isCollapsed && (
              <span className="items-center hidden sm:flex text-base lg:text-xl font-bold text-brand dark:text-brand-light hover:text-brand-light dark:hover:text-brand transition-colors whitespace-nowrap">
                {window.location.hostname.includes("voicerun") ? "Voice Run" : "Prim Voices"}
              </span>
            )}
          </Link>
        </View>

        {/* Search trigger button */}
        <View className={clsx("mb-4", isCollapsed ? "px-2" : "px-3")}>
          {!isCollapsed ? (
            <button
              type="button"
              onClick={openCommandPalette}
              className={clsx(
                "w-full flex items-center justify-between",
                "px-3 py-2 rounded-lg",
                "bg-gray-100 dark:bg-[#0a0a0a]",
                "border border-gray-200 dark:border-[#1a1a1a]",
                "text-gray-500 dark:text-gray-400",
                "hover:bg-gray-200 dark:hover:bg-[#1a1a1a]",
                "hover:border-gray-300 dark:hover:border-[#333]",
                "transition-colors",
              )}
            >
              <View className="flex items-center gap-2">
                <Icon
                  name="search"
                  size={16}
                />
                <Text className="text-sm">Search...</Text>
              </View>
              <View className="flex items-center gap-0.5 text-xs">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-medium">⌘</kbd>
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-medium">K</kbd>
              </View>
            </button>
          ) : (
            <button
              type="button"
              onClick={openCommandPalette}
              title="Search (⌘K)"
              className={clsx(
                "w-full flex items-center justify-center",
                "p-2 rounded-lg",
                "bg-gray-100 dark:bg-[#0a0a0a]",
                "border border-gray-200 dark:border-[#1a1a1a]",
                "text-gray-500 dark:text-gray-400",
                "hover:bg-gray-200 dark:hover:bg-[#1a1a1a]",
                "hover:border-gray-300 dark:hover:border-[#333]",
                "transition-colors",
              )}
            >
              <Icon
                name="search"
                size={18}
              />
            </button>
          )}
        </View>

        <View className={clsx("flex-1 overflow-y-auto", isCollapsed ? "px-2" : "px-3")}>
          {sections.map((section, sectionIdx) => (
            <View
              key={section.title || sectionIdx}
              className={clsx(sectionIdx > 0 && section.title && "mt-8")}
            >
              {/* Add divider if this is a section with no title following a section with title */}
              {sectionIdx > 0 && !section.title && sections[sectionIdx - 1]?.title && (
                <View className="mx-3 my-4 border-t border-gray-200 dark:border-[#1a1a1a]" />
              )}
              {!isCollapsed && section.title && (
                <Text className="px-3 mb-3 text-[10px] font-semibold text-gray-500 dark:text-[#555] tracking-widest uppercase">{section.title}</Text>
              )}
              <View className="space-y-1">
                {section.links.map((link) => {
                  const isActive = isLinkActive(link.to);
                  return isCollapsed ? (
                    <div
                      key={link.to}
                      title={link.label === "organizations" ? "Organization" : link.label}
                    >
                      <Link
                        to={link.to}
                        onClick={() => onClose && onClose()}
                        className={clsx(
                          "relative flex items-center justify-center py-2.5 rounded-md transition-all duration-200",
                          isActive
                            ? "bg-gray-200 dark:bg-[#0a0a0a] text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand dark:before:bg-cyan-400 before:rounded-r"
                            : "text-gray-600 dark:text-[#666] hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/50",
                        )}
                      >
                        <Icon
                          name={
                            link.label === "Agents"
                              ? "bot"
                              : link.label === "Evaluators"
                                ? "checklist"
                                : link.label === "Simulations"
                                  ? "play"
                                  : link.label === "Recipes"
                                    ? "recipe"
                                    : link.label === "Organization"
                                      ? "users"
                                      : link.label === "Resources"
                                        ? "folder_open"
                                        : link.label === "Admin"
                                          ? "admin_panel_settings"
                                          : link.label === "Profile"
                                            ? "person"
                                            : link.label === "Users"
                                              ? "users"
                                              : link.label === "Organizations"
                                                ? "grid"
                                                : link.label === "Training Jobs"
                                                  ? "school"
                                                  : link.label === "Voice Library"
                                                    ? "voice-over"
                                                    : "folder"
                          }
                          size={20}
                        />
                      </Link>
                    </div>
                  ) : (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => onClose && onClose()}
                      className={clsx(
                        "relative block px-5 py-2.5 rounded-md text-[16px] font-medium transition-all duration-200",
                        isActive
                          ? "bg-gray-200 dark:bg-[#0a0a0a] text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand dark:before:bg-cyan-400 before:rounded-r"
                          : "text-gray-600 dark:text-[#666] hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/50",
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </View>
            </View>
          ))}
        </View>

        {/* Utilities row - Theme toggle, Profile, and Notifications */}
        <View
          className={clsx("user-menu-container relative border-t border-gray-200 dark:border-[#1a1a1a] bg-gray-50 dark:bg-black", isCollapsed ? "p-2" : "p-3")}
        >
          <View className={clsx("flex", isCollapsed ? "flex-col items-center gap-2" : "items-center justify-evenly gap-2")}>
            {/* Theme Toggle */}
            <Button
              type="button"
              variant="ghost"
              onClick={toggleTheme}
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className="!p-0 !w-full rounded-md transition-colors bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80 flex justify-center"
            >
              <Icon
                name={theme === "light" ? "moon" : "sun"}
                size={20}
                className="text-gray-500 dark:text-[#555]"
              />
            </Button>

            {/* Profile Button */}
            <div
              ref={userButtonRef}
              title={user?.name || "User"}
              className="w-full"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="!p-0 !w-full rounded-md transition-colors bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80 flex justify-center"
                title={user?.name || "User"}
              >
                <Icon
                  name="user"
                  size={20}
                  className="text-gray-500 dark:text-[#555]"
                />
              </Button>
            </div>

            {/* Notification Bell - admin only */}
            {user?.isAdmin && (
              <NotificationBell
                variant="outlined"
                fullWidth={isCollapsed}
              />
            )}
          </View>

          {/* Profile Dropdown - positioned relative to full container */}
          {showUserMenu && !isCollapsed && (
            <View className="absolute bottom-full left-0 right-0 mx-3 mb-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-50">
              <View className="p-3 border-b border-gray-200 dark:border-gray-700">
                <Text className="text-sm font-semibold text-gray-900 dark:text-white">Account</Text>
              </View>
              <View className="py-1">
                <Link
                  to="/profile"
                  className="block px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => {
                    setShowUserMenu(false);
                    onClose && onClose();
                  }}
                >
                  Profile Settings
                </Link>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    onClose && onClose();
                    navigate("/signout");
                  }}
                  className="w-full text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Sign out
                </button>
              </View>
            </View>
          )}

          {/* Organization Changer - Only show for admin users */}
          {user?.isAdmin && (
            <View className="mt-3 org-dropdown-container">
              <View className="relative">
                <div
                  ref={orgButtonRef}
                  title={isCollapsed ? "Organization" : undefined}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                    className={clsx(
                      "w-full rounded-md bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80 transition-colors flex items-center",
                      isCollapsed ? "p-2 justify-start" : "pl-2 pr-3 py-2.5 justify-between",
                    )}
                  >
                    {isCollapsed ? (
                      <Icon
                        name="grid"
                        size={20}
                        className="text-gray-500 dark:text-[#555]"
                      />
                    ) : (
                      <>
                        <View className="flex items-center gap-2 min-w-0">
                          <Icon
                            name="grid"
                            size={16}
                            className="text-gray-500 dark:text-[#555] flex-shrink-0"
                          />
                          <Text className="text-sm font-medium text-gray-900 dark:text-white text-left flex-1 whitespace-normal break-words line-clamp-2">
                            {organization?.name || "Select Organization"}
                          </Text>
                        </View>
                        <Icon
                          name={showOrgDropdown ? "chevron-up" : "chevron-down"}
                          size={16}
                          className="text-gray-500 dark:text-[#555] flex-shrink-0"
                        />
                      </>
                    )}
                  </Button>
                </div>

                {showOrgDropdown && !isCollapsed && (
                  <View className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] rounded-md shadow-lg overflow-hidden z-50">
                    <View className="p-3">
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-200 dark:border-[#1a1a1a] rounded text-gray-900 dark:text-white bg-gray-50 dark:bg-[#1a1a1a] placeholder-gray-400 dark:placeholder-[#555] text-sm"
                        placeholder="Search organizations..."
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        autoFocus
                      />
                    </View>
                    <View className="max-h-48 overflow-y-auto">
                      {sortedOrganizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => handleOrgSelect(org.id)}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-[#666] hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors flex items-center justify-between"
                        >
                          <span>{org.name}</span>
                        </button>
                      ))}
                      {sortedOrganizations.length === 0 && <View className="px-4 py-2.5 text-sm text-gray-500 dark:text-[#555]">No organizations found</View>}
                    </View>
                  </View>
                )}
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Portal for user menu when collapsed */}
      {showUserMenu &&
        isCollapsed &&
        userButtonRef.current &&
        ReactDOM.createPortal(
          <View
            className="user-menu-portal fixed bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-[10000] w-48"
            style={{
              top: userButtonRef.current.getBoundingClientRect().top - 160,
              left: (sidebarRef.current?.getBoundingClientRect().left ?? 0) + 12,
            }}
          >
            <View className="p-3 border-b border-gray-200 dark:border-gray-700">
              <Text className="text-sm font-semibold text-gray-900 dark:text-white">Account</Text>
            </View>
            <View className="py-1">
              <Link
                to="/profile"
                className="block px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => {
                  setShowUserMenu(false);
                  onClose && onClose();
                }}
              >
                Profile Settings
              </Link>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  onClose && onClose();
                  navigate("/signout");
                }}
                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Sign out
              </button>
            </View>
          </View>,
          document.body,
        )}

      {/* Portal for org dropdown when collapsed */}
      {showOrgDropdown &&
        isCollapsed &&
        orgButtonRef.current &&
        ReactDOM.createPortal(
          <View
            className="org-dropdown-portal fixed bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-[10000] w-64"
            style={{
              top: orgButtonRef.current.getBoundingClientRect().top - 200,
              left: (sidebarRef.current?.getBoundingClientRect().left ?? 0) + 12,
            }}
          >
            <View className="p-3">
              <input
                type="text"
                className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 text-sm"
                placeholder="Search organizations..."
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                autoFocus
              />
            </View>
            <View className="max-h-48 overflow-y-auto">
              {sortedOrganizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrgSelect(org.id)}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between"
                >
                  <span>{org.name}</span>
                </button>
              ))}
              {sortedOrganizations.length === 0 && <View className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">No organizations found</View>}
            </View>
          </View>,
          document.body,
        )}
    </>
  );
};
